import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const AllStudentsResultsDisplay = ({
  students,
  onEdit,
  onDelete,
  onAddSemester,
  // 🆕 optional: per-semester delete callback
  onDeleteSemester,
  expandedStudents,
  setExpandedStudents,
  hasInitialized,
}) => {
  const navigate = useNavigate();

  // Group rows by candidate (indexNo)
  const groupedCandidates = useMemo(() => {
    const map = new Map();

    (students || []).forEach((s) => {
      const key = s.indexNo || s.candidateId || s.id;
      if (!key) return;

      if (!map.has(key)) {
        map.set(key, {
          key,
          indexNo: s.indexNo,
          candidateName: s.candidateName,
          candidateId: s.candidateId,
          course: s.course || s.classSelected,
          base: s,
          dynamic_fields: s.dynamic_fields,
          semesters: [],
        });
      }
      map.get(key).semesters.push(s);
    });

    // sort semesters inside each candidate
    const arr = Array.from(map.values());
    arr.forEach((c) => {
      c.semesters.sort((a, b) => {
        const sa = parseInt(a.semester, 10) || 0;
        const sb = parseInt(b.semester, 10) || 0;
        return sa - sb;
      });
    });
    return arr;
  }, [students]);

  // Auto-expand first candidate if only one
  useEffect(() => {
    if (!hasInitialized.current && groupedCandidates.length === 1) {
      const firstKey = groupedCandidates[0].key;
      const nextSet = new Set();
      nextSet.add(firstKey);
      setExpandedStudents(nextSet);
      hasInitialized.current = true;
    }
  }, [groupedCandidates, hasInitialized, setExpandedStudents]);

  const toggleExpand = (candidateKey) => {
    const next = new Set(expandedStudents);
    if (next.has(candidateKey)) {
      next.delete(candidateKey);
    } else {
      next.add(candidateKey);
    }
    setExpandedStudents(next);
  };

  const handleViewResult = (semesterRow) => {
    if (!semesterRow.indexNo) {
      alert('Index / Reg. No not found for this student.');
      return;
    }

    localStorage.setItem('resultIndexNo', semesterRow.indexNo);
    localStorage.setItem('resultSemester', semesterRow.semester || '');

    navigate('/result', {
      state: {
        indexNo: semesterRow.indexNo,
        semester: semesterRow.semester || '',
      },
    });
  };

const handleViewDigiLocker = (semesterRow) => {
  if (!semesterRow.indexNo) {
    alert('Index / Reg. No not found for this student.');
    return;
  }

  const idx = semesterRow.indexNo;
  const sem = semesterRow.semester || '';

  // 🔹 Store in localStorage (covers old implementations)
  localStorage.setItem('resultIndexNo', idx);
  localStorage.setItem('resultSemester', sem);
  localStorage.setItem('indexNo', idx);
  localStorage.setItem('indexNumber', idx);
  localStorage.setItem('digilockerIndexNo', idx);
  localStorage.setItem('digilockerSemester', sem);

  // 🔹 Build URL with query params (what your DigiLocker page most likely expects)
  const url = `/digilocker/result?indexNo=${encodeURIComponent(
    idx
  )}&semester=${encodeURIComponent(sem)}`;

  // 🔹 Also pass via state (for a more modern pattern / future use)
  navigate(url, {
    state: {
      indexNo: idx,
      indexNumber: idx,
      rollNo: idx,
      semester: sem,
    },
  });
};
  // (We keep calcSemesterTotal in case you want it later, but no longer show it in UI)
  const calcSemesterTotal = (student) => {
    const subs = student.subjects || [];
    if (!subs.length) return null;

    let total = 0;
    let hasNumeric = false;

    subs.forEach((sub) => {
      const internal = sub.practical ?? sub.internalMarks;
      const external = sub.theory ?? sub.externalMarks;

      const iNum =
        internal == null || internal === '' ? null : Number(internal);
      const eNum =
        external == null || external === '' ? null : Number(external);

      if (iNum != null || eNum != null) {
        total += (isNaN(iNum) ? 0 : iNum) + (isNaN(eNum) ? 0 : eNum);
        hasNumeric = true;
      }
    });

    return hasNumeric ? total : null;
  };

  if (!students || students.length === 0) {
    return (
      <div className="text-center py-6 text-sm text-gray-500">
        No student records found. Add a student to get started.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groupedCandidates.map((candidate, index) => {
        const isExpanded = expandedStudents.has(candidate.key);
        const base = candidate.base;
        const courseLabel =
          candidate.course || base.classSelected || 'Course not specified';

        return (
          <div
            key={candidate.key}
            className={`border rounded-2xl shadow-sm overflow-hidden ${
              index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
            }`}
          >
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:items-center justify-between px-4 md:px-5 py-3 gap-3 border-b border-gray-200">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <button
                    type="button"
                    onClick={() => toggleExpand(candidate.key)}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
                  >
                    <span className="material-symbols-outlined text-[18px]">
                      {isExpanded ? 'expand_less' : 'expand_more'}
                    </span>
                  </button>
                  <h3 className="text-sm md:text-base font-semibold text-gray-900 truncate">
                    {candidate.candidateName || 'Unnamed Candidate'}
                  </h3>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-[11px] md:text-xs text-gray-600">
                  {/* Index / Reg */}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200">
                    <span className="material-symbols-outlined text-[14px]">
                      badge
                    </span>
                    <span>
                      Index / Reg. No:&nbsp;
                      <span className="font-semibold text-gray-800">
                        {candidate.indexNo || 'N/A'}
                      </span>
                    </span>
                  </span>

                  {/* Course */}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100">
                    <span className="material-symbols-outlined text-[14px]">
                      school
                    </span>
                    <span className="font-medium text-blue-800 truncate max-w-[160px] md:max-w-xs">
                      {courseLabel}
                    </span>
                  </span>

                  {/* Total semesters */}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100">
                    <span className="material-symbols-outlined text-[14px]">
                      auto_stories
                    </span>
                    <span className="font-medium text-emerald-800">
                      {candidate.semesters.length} Semester
                      {candidate.semesters.length > 1 ? 's' : ''}
                    </span>
                  </span>
                </div>
              </div>

              {/* TOP RIGHT – ADD SEMESTER + DELETE CANDIDATE */}
              <div className="flex flex-wrap items-center gap-2 justify-start md:justify-end">
                <button
                  type="button"
                  onClick={() =>
                    onAddSemester && onAddSemester(candidate.semesters[0].id)
                  }
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700"
                >
                  <span className="material-symbols-outlined text-[16px]">
                    add
                  </span>
                  Add Another Semester
                </button>

                <button
                  type="button"
                  onClick={() =>
                    onDelete && onDelete(candidate.semesters[0].id)
                  }
                  className="inline-flex items-center gap-1 rounded-full bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-700"
                >
                  <span className="material-symbols-outlined text-[16px]">
                    delete
                  </span>
                  Delete Candidate
                </button>
              </div>
            </div>

            {/* EXPANDED CONTENT */}
            {isExpanded && (
              <div className="px-4 md:px-5 py-4 bg-white">
                {/* Candidate core details (from first row) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs md:text-sm mb-4">
                  <div className="space-y-1">
                    <p className="font-semibold text-gray-700">
                      Candidate Details
                    </p>
                    <div className="grid grid-cols-[110px_1fr] gap-y-1 text-[11px] md:text-xs">
                      <span className="text-gray-500">Name:</span>
                      <span className="font-medium text-gray-900">
                        {candidate.candidateName || 'N/A'}
                      </span>

                      <span className="text-gray-500">
                        University Roll:
                      </span>
                      <span>{candidate.candidateId || 'N/A'}</span>

                      <span className="text-gray-500">
                        Index / Reg. No:
                      </span>
                      <span>{candidate.indexNo || 'N/A'}</span>

                      <span className="text-gray-500">Course:</span>
                      <span>{courseLabel}</span>
                    </div>
                  </div>

                  {/* Dynamic fields */}
                  {/* <div className="space-y-1">
                    <p className="font-semibold text-gray-700">
                      Additional Info
                    </p>
                    <div className="grid grid-cols-[110px_1fr] gap-y-1 text-[11px] md:text-xs">
                      {candidate.dynamic_fields &&
                        Object.keys(candidate.dynamic_fields).map((key) => (
                          <React.Fragment key={key}>
                            <span className="text-gray-500 capitalize">
                              {key.replace(/[_\-]/g, ' ')}:
                            </span>
                            <span>
                              {String(candidate.dynamic_fields[key])}
                            </span>
                          </React.Fragment>
                        ))}
                    </div>
                  </div> */}
                </div>

                {/* SEMESTER RESULT SUMMARY – ONE ROW PER SEMESTER */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">
                    Semester Result Summary
                  </h4>

                  <div className="space-y-2">
                    {candidate.semesters.map((semRow, idx) => {
                      const isFinalSem =
                        semRow.isFinalSemester ??
                        semRow.is_final_semester ??
                        false;

                      return (
                        <div
                          key={semRow.id || idx}
                          className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 flex flex-col md:flex-row md:items-center md:justify-between gap-2"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-800">
                                <span className="material-symbols-outlined text-[14px]">
                                  auto_stories
                                </span>
                                Semester {semRow.semester || '(N/A)'}
                              </span>

                              {semRow.examMonth && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100 text-blue-800">
                                  <span className="material-symbols-outlined text-[14px]">
                                    calendar_month
                                  </span>
                                  {semRow.examMonth}{' '}
                                  {semRow.resultYear
                                    ? `, ${semRow.resultYear}`
                                    : ''}
                                </span>
                              )}

                              {semRow.result && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-800">
                                  <span className="material-symbols-outlined text-[14px]">
                                    checklist_rtl
                                  </span>
                                  {semRow.result}
                                </span>
                              )}

                              {isFinalSem && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 border border-purple-200 text-purple-800">
                                <span className="material-symbols-outlined text-[14px]">
                                  star
                                </span>
                                Final Semester
                              </span>
                            )}
                            </div>
                          </div>

                          {/* ACTIONS – view, digilocker, edit, delete semester */}
                          <div className="flex flex-wrap items-center gap-2 justify-start md:justify-end">
                            <button
                              type="button"
                              onClick={() => handleViewResult(semRow)}
                              className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700"
                            >
                              <span className="material-symbols-outlined text-[16px]">
                                visibility
                              </span>
                              View Result
                            </button>

                            <button
                              type="button"
                              onClick={() => handleViewDigiLocker(semRow)}
                              className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700"
                            >
                              <span className="material-symbols-outlined text-[16px]">
                                account_balance
                              </span>
                              View DigiLocker
                            </button>

                            <button
                              type="button"
                              onClick={() => onEdit && onEdit(semRow.id)}
                              className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-amber-600"
                            >
                              <span className="material-symbols-outlined text-[16px]">
                                edit
                              </span>
                              Edit
                            </button>

                            {/* 🆕 per-semester delete */}
                            <button
                              type="button"
                              onClick={() =>
                                onDeleteSemester &&
                                onDeleteSemester(semRow)
                              }
                              className="inline-flex items-center gap-1 rounded-full bg-red-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-600"
                            >
                              <span className="material-symbols-outlined text-[16px]">
                                delete
                              </span>
                              Delete Semester
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* You can still add a mini subjects preview here if needed */}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AllStudentsResultsDisplay;